import {Component, OnInit} from '@angular/core';
import {FormsModule, NgForm} from '@angular/forms';
import {finalize} from 'rxjs';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faBuilding,
  faCheck,
  faEnvelope,
  faEye,
  faEyeSlash,
  faFloppyDisk,
  faKey,
  faLocationDot,
  faLock,
  faPhone,
  faRotateLeft,
  faShieldHalved,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import {SettingsService} from '../../../core/services/settings.service';
import {
  ChangePasswordRequest,
  ClientSettingsModel,
  UpdateClientSettingsRequest
} from '../../../core/models/settings.model';
import {getApiErrorMessage} from '../../../core/utils/api-error-message';
import {ErrorAlertComponent} from '../../components/error-alert.component/error-alert.component';
import {LoadingSpinnerComponent} from '../../components/loading-spinner.component/loading-spinner.component';

type SettingsTab = 'client' | 'security';

@Component({
  selector: 'app-settings',
  imports: [FormsModule, FaIconComponent, ErrorAlertComponent, LoadingSpinnerComponent],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.css'
})
export class SettingsComponent implements OnInit {
  activeTab: SettingsTab = 'client';
  settings: ClientSettingsModel | null = null;
  clientForm: UpdateClientSettingsRequest = this.emptyClientForm();
  passwordForm: ChangePasswordRequest = this.emptyPasswordForm();

  loading = false;
  loadError = '';
  savingClient = false;
  clientError = '';
  clientSuccess = '';
  savingPassword = false;
  passwordError = '';
  passwordSuccess = '';
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  readonly passwordPattern = '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d).{6,30}$';
  readonly phonePattern = '^\\+?[0-9]+$';
  readonly faBuilding = faBuilding;
  readonly faCheck = faCheck;
  readonly faEnvelope = faEnvelope;
  readonly faEye = faEye;
  readonly faEyeSlash = faEyeSlash;
  readonly faFloppyDisk = faFloppyDisk;
  readonly faKey = faKey;
  readonly faLocationDot = faLocationDot;
  readonly faLock = faLock;
  readonly faPhone = faPhone;
  readonly faRotateLeft = faRotateLeft;
  readonly faShieldHalved = faShieldHalved;
  readonly faUser = faUser;

  private savedClientForm: UpdateClientSettingsRequest = this.emptyClientForm();

  constructor(private readonly settingsService: SettingsService) {}

  ngOnInit(): void {
    this.loadSettings();
  }

  selectTab(tab: SettingsTab): void {
    this.activeTab = tab;
  }

  loadSettings(): void {
    this.loading = true;
    this.loadError = '';

    this.settingsService.getSettings()
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: settings => {
          this.settings = settings;
          this.setClientForm(settings);
        },
        error: error => {
          this.loadError = getApiErrorMessage(
            error,
            'Податоците за поставките не може да се вчитаат.'
          );
        }
      });
  }

  saveClient(form: NgForm): void {
    if (form.invalid || this.savingClient) return;

    this.savingClient = true;
    this.clientError = '';
    this.clientSuccess = '';

    const request: UpdateClientSettingsRequest = {
      name: this.clientForm.name.trim(),
      location: this.clientForm.location.trim(),
      email: this.clientForm.email.trim(),
      phoneNumber: this.clientForm.phoneNumber.trim()
    };

    this.settingsService.updateClient(request)
      .pipe(finalize(() => this.savingClient = false))
      .subscribe({
        next: settings => {
          this.settings = settings;
          this.setClientForm(settings);
          form.resetForm(this.clientForm);
          this.clientSuccess = 'Податоците за клиентот се успешно зачувани.';
        },
        error: error => {
          this.clientError = getApiErrorMessage(
            error,
            'Податоците за клиентот не може да се зачуваат.'
          );
        }
      });
  }

  resetClient(form: NgForm): void {
    this.clientForm = {...this.savedClientForm};
    form.resetForm(this.clientForm);
    this.clientError = '';
    this.clientSuccess = '';
  }

  updatePhoneNumber(value: string): void {
    this.clientForm.phoneNumber = this.normalizePhoneNumber(value);
  }

  changePassword(form: NgForm): void {
    this.passwordError = '';
    this.passwordSuccess = '';

    if (form.invalid || this.passwordsDoNotMatch || this.savingPassword) return;

    this.savingPassword = true;
    this.settingsService.changePassword({...this.passwordForm})
      .pipe(finalize(() => this.savingPassword = false))
      .subscribe({
        next: () => {
          this.passwordForm = this.emptyPasswordForm();
          form.resetForm(this.passwordForm);
          this.showCurrentPassword = false;
          this.showNewPassword = false;
          this.showConfirmPassword = false;
          this.passwordSuccess = 'Лозинката е успешно променета. Другите активни сесии се одјавени.';
        },
        error: error => {
          this.passwordError = getApiErrorMessage(
            error,
            'Лозинката не може да се промени.'
          );
        }
      });
  }

  get passwordsDoNotMatch(): boolean {
    return this.passwordForm.confirmNewPassword.length > 0
      && this.passwordForm.newPassword !== this.passwordForm.confirmNewPassword;
  }

  hasMinimumLength(): boolean {
    return this.passwordForm.newPassword.length >= 6;
  }

  hasLowercaseLetter(): boolean {
    return /[a-z]/.test(this.passwordForm.newPassword);
  }

  hasUppercaseLetter(): boolean {
    return /[A-Z]/.test(this.passwordForm.newPassword);
  }

  hasNumber(): boolean {
    return /\d/.test(this.passwordForm.newPassword);
  }

  private setClientForm(settings: ClientSettingsModel): void {
    const values: UpdateClientSettingsRequest = {
      name: settings.name,
      location: settings.location,
      email: settings.email,
      phoneNumber: this.normalizePhoneNumber(settings.phoneNumber)
    };
    this.savedClientForm = {...values};
    this.clientForm = {...values};
  }

  private emptyClientForm(): UpdateClientSettingsRequest {
    return {name: '', location: '', email: '', phoneNumber: ''};
  }

  private emptyPasswordForm(): ChangePasswordRequest {
    return {currentPassword: '', newPassword: '', confirmNewPassword: ''};
  }

  private normalizePhoneNumber(value: string): string {
    const rawValue = value ?? '';
    const prefix = rawValue.startsWith('+') ? '+' : '';
    return `${prefix}${rawValue.replace(/\D/g, '')}`;
  }
}
